import React from 'react';
import Card from './UIKit/Card';
import Button from './UIKit/Button';
import { Box, Typography, Chip } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PendingIcon from '@mui/icons-material/Pending';
import { useTranslation } from 'react-i18next';

const TestResultCard = ({ result, onRetake, onViewDetails }) => {
  const { t } = useTranslation(['tests', 'common']);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: { xs: 1.5, sm: 2 },
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: 'background.paper',
          opacity: 0.95,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: { xs: 1, sm: 2 },
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 0.5,
              fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
            }}
          >
            {result.test?.title || 'Неизвестный тест'}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.813rem' } }}
          >
            {new Date(
              result.completedAt || result.startedAt
            ).toLocaleDateString('ru-RU')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexDirection: 'column',
          }}
        >
          {result.completed && (
            <Typography
              variant="h5"
              color={
                result.hasPendingReview
                  ? 'warning.main'
                  : result.passed
                  ? 'success.main'
                  : 'error.main'
              }
              sx={{
                fontWeight: 600,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
              }}
            >
              {(result.score ?? 0).toFixed(0)}%
            </Typography>
          )}
          <Chip
            icon={
              !result.completed
                ? <ErrorOutlineIcon />
                : result.hasPendingReview
                ? <PendingIcon />
                : null
            }
            label={
              result.completed
                ? result.hasPendingReview
                  ? t('tests:results.pending_review')
                  : result.passed
                  ? t('tests:results.passed')
                  : t('tests:results.failed')
                : t('tests:results.not_completed')
            }
            color={
              result.completed
                ? result.hasPendingReview
                  ? 'warning'
                  : result.passed
                  ? 'success'
                  : 'error'
                : 'warning'
            }
            size="small"
            sx={{
              minWidth: 90,
              '& .MuiChip-label': {
                fontSize: { xs: '0.75rem', sm: '0.813rem' },
              },
            }}
          />
          {result.completed && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.813rem' } }}
            >
              {result.correctAnswers} / {result.totalQuestions}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {result.completed ? (
          <>
            <Button
              variant="neutral"
              onClick={() => onRetake(result.test)}
              sx={(theme) => ({
                bgcolor: theme.palette.action.hover,
                '&:hover': { bgcolor: theme.palette.action.selected },
              })}
              fullWidth
            >
              {t('tests:results.try_again')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => onViewDetails(result._id)}
              fullWidth
            >
              {t('tests:results.details')}
            </Button>
          </>
        ) : (
          <Button
            variant="outlined"
            onClick={() => onRetake(result.test)}
            fullWidth
          >
            {t('tests:results.continue')}
          </Button>
        )}
      </Box>
    </Card>
  );
};

export default TestResultCard;
